import { Reel } from "@lib/titan2-slingo/dist/game/view/abstract/display/Reel";
import { ReelSpinSymbol } from "@lib/titan2-slingo/dist/game/view/abstract/display/ReelSpinSymbol";
import { SlingoStyle } from "@lib/titan2-slingo/dist/game/view/abstract/style/SlingoStyle";
import { TitanBitmapTextField } from "@lib/titan2/dist/game/control/TitanBitmapTextField";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { SpriteProperties } from "@lib/titan2/dist/game/control/properties/SpriteProperties";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { gsap } from "gsap";
import { GameStyle } from "../style/GameStyle";
import { GameReelSymbol } from "./GameReelSymbol";
import { addToTitanSpineSlot } from "../../../utils/SpineUtils";
/**
 * GameReel.ts
 */
export class GameReel extends Reel {
    create() {
        super.create();
        //TODO: update createField to detect BitmapTextFieldProperties for auto-creation
        DestroyUtil.destroy(this.symbol);
        this.symbol = new TitanBitmapTextField(this.landscapeProperties.symbolProperties, this.portraitProperties?.symbolProperties);
        this.symbolHighlight = new TitanBitmapTextField(this.landscapeProperties.symbolHighlightProperties, this.portraitProperties?.symbolHighlightProperties);
        this.symbolHolder.addChild(this.symbol, this.symbolHighlight);
        this.updateSymbolNumber("1");
    }
    updateSymbolNumber(id) {
        if (this.symbolNumber) {
            this.symbolNumber.destroy();
        }
        const index = parseInt(id) - 1;
        this.symbolNumber = TitanSprite.createSprite(undefined, `reel${id}`, this.landscapeProperties.numberSprites[index], this.portraitProperties?.numberSprites[index]);
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.symbolHighlight);
        this.symbolHighlight = null;
    }
    createReelSymbols(reelSet) {
        return reelSet.map(id => {
            // Don't remake a symbol if it's already part of reelSetSymbolsStandard0
            const existingSymbol = this.getReelSymbols(id);
            if (existingSymbol) {
                return existingSymbol;
            }
            let item;
            if (isNaN(id) && this.landscapeProperties.symbolIDToStaticMap[id]) {
                item = TitanSprite.createSprite(this.reelSetSymbolHolder, "item", new SpriteProperties(SlingoStyle.REEL_SYMBOL + this.landscapeProperties.symbolIDToStaticMap[id], SlingoStyle.MODULE), undefined);
                item.position.set(0, 0);
            }
            else {
                const index = parseInt(id) - 1;
                item = TitanSprite.createSprite(undefined, `reel${id}`, this.landscapeProperties.numberSprites[index], this.portraitProperties?.numberSprites[index]);
                this.reelSetSymbolHolder.addChild(item);
                item.position.set(80, 80);
            }
            return new ReelSpinSymbol(id, item);
        });
    }
    createModel() {
        this.model = this.factory.getModel();
        this.responsibleGaming = this.model.getResponsibleGaming();
    }
    createSymbolAnimations() {
        this.symbolAnimation = new GameReelSymbol(this.landscapeProperties.symbolAnimationSpineProperties, this.portraitProperties?.symbolAnimationSpineProperties, this.uiProperties);
    }
    addSymbol(id) {
        this.isDisabled = false;
        this.symbol.visible = false;
        this.symbolNumber.visible = false;
        switch (id) {
            case GameStyle.DEVIL:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeDevil();
                break;
            case GameStyle.COIN:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeCoin();
                break;
            case GameStyle.JOKER:
                this.symbolHolder.addChild(this.symbolAnimation);
                if (this.columnFullyDabbed()) {
                    this.symbolAnimation.strikeJokerFullyDabbed();
                }
                else {
                    this.symbolAnimation.strikeJoker();
                }
                break;
            case GameStyle.SUPER_JOKER:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeSuperJoker();
                break;
            case GameStyle.FREE_SPIN:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.symbolAnimation.strikeFreeSpin();
                break;
            default:
                this.symbolHolder.addChild(this.symbolAnimation);
                this.updateSymbolNumber(id);
                this.symbolAnimation.stopNumber();
                addToTitanSpineSlot(this.symbolAnimation.getSpine(), this.symbolNumber, "dynamic-slot");
                this.symbolNumber.visible = true;
                if (this.idMatchesTicket(id)) {
                    this.symbolAnimation.strikeNumber();
                }
        }
        this.currentId = id;
        //add a little bump when it stops
        gsap.to(this.symbolHolder, { y: "+=5", yoyo: true, repeat: 1, duration: 0.05 });
    }
    enable() {
        super.enable();
        this.isDisabled = false;
    }
    disable() {
        super.disable();
        this.isDisabled = true;
    }
    collectNumber() {
        this.symbolHolder.addChildAt(this.symbolAnimation, 0);
        this.symbolAnimation.collectNumber();
    }
    collectFreeSpin() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectFreeSpin();
    }
    collectCoinWin() {
        this.symbolHolder.addChild(this.symbolAnimation);
        this.symbolAnimation.collectCoinWin();
    }
    idMatchesTicket(id) {
        const ticketDab = this.model.getDabData();
        const autoHighlightCells = ticketDab.getAutoHighlightCells();
        return autoHighlightCells.findIndex(cell => cell.getNumber() === parseInt(id)) > -1;
    }
    addDisabled() {
        this.isDisabled = true;
        if (this.symbolAnimation) {
            switch (this.currentId) {
                case GameStyle.JOKER:
                    this.symbolAnimation.stopJoker();
                    break;
                case GameStyle.SUPER_JOKER:
                    this.symbolAnimation.stopSuperJoker();
                    break;
                case GameStyle.FREE_SPIN:
                    this.symbolAnimation.stopFreeSpin();
                    break;
                case GameStyle.COIN:
                    this.symbolAnimation.stopCoin();
                    break;
                case GameStyle.DEVIL:
                    this.symbolAnimation.stopDevil();
                    break;
                default:
                    this.symbolAnimation.stopNumber();
                    break;
            }
        }
        this.addChild(this.disabled);
    }
    animateJoker() {
        if (!this.isDisabled && !this.columnFullyDabbed()) {
            this.symbolAnimation.loopJoker();
        }
    }
    animateSuperJoker() {
        if (!this.isDisabled) {
            this.symbolAnimation.loopSuperJoker();
        }
    }
    removeSymbol() {
        this.symbol.visible = false;
        this.symbolHighlight.visible = false;
    }
    removeSymbolAnimation() {
        if (this.symbolAnimation) {
            this.symbolHolder.removeChild(this.symbolAnimation);
        }
    }
}
//# sourceMappingURL=GameReel.js.map